import React, { useEffect, useState, useCallback, useRef } from "react";
import { injectIntl } from "react-intl";
import { withRouter, Link } from "react-router-dom";
import Routes from "../../Routes";

// REDUX
import { useSelector, useDispatch } from "react-redux";
import ACTIONS from "../../actions/types";

// HOC
import NewAppHOC from "../../hoc/NewAppHOC";
import ErrorHOC from "../../hoc/ErrorHOC";

// COMPONENTS
import MainAsideNav from "../../components/navigations/MainAsideNav";
import HelperAsideNav from "../../components/navigations/HelperAsideNav";
import Main from "../../components/main/Main";
import AlphaTable from "../../components/tables/AlphaTable";

import DisplayScore from "../../components/display/DisplayScore";
import UserNav from "../../components/navigations/UserNav";
import Title from "../../components/text/Title";
import Layout from "../../components/layout/Layout";
import Icon from "../../components/icons/Icon";
import ReviewForm from "../../components/forms/ReviewForm";

// FUNCTIONS
import AJAX from "../../functions/ajax";
import validateData from "../../functions/validateData";

// DATA MODEL
import reviewerAssignmentsDataModel from "../../data_model/review_admin/reviewer_case/case_data.js";

function ReviewerAssignments({ match, info, history, intl, location }) {
  const isInitialMount = useRef(false);
  const _id = 12908319203891;
  const dispatch = useDispatch();

  const [counterData, setCounterData] = useState();
  const [tableData, setTableData] = useState();
  const [formData, setFormData] = useState();
  const [score, setScore] = useState();
  const [ccaseID, setCcaseID] = useState();
  const [stageInfo, setStageInfo] = useState();
  const [reviewerInfo, setReviewerInfo] = useState();
  const [reviewers, setReviewers] = useState();
  const [initialFilter, setInitialFilter] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const reduxState = useSelector(state => state);
  const sizeOfTheScreen = useSelector(store => store.app.sizeOfTheScreen);

  // Parse filter from URL query params
  const getFilterFromUrl = useCallback(() => {
    const searchParams = new URLSearchParams(location?.search || "");
    const filterParam = searchParams.get("filter");
    if (filterParam) {
      try {
        return JSON.parse(decodeURIComponent(filterParam));
      } catch (e) {
        console.warn("Failed to parse filter from URL:", e);
        return null;
      }
    }
    return null;
  }, [location?.search]);

  const getMainData = useCallback(() => {
    const { stage_id, profile_id } = match.params;
    const urlFilter = getFilterFromUrl();
    if (urlFilter) {
      setInitialFilter(urlFilter);
      setCurrentFilter(urlFilter);
    } else {
      setCurrentFilter(null);
    }

    AJAX()
      .getReviewerAssignments(_id, reduxState, stage_id, profile_id, urlFilter)
      .then(res => {
        // Axios wraps response in .data
        const apiResponse = res.data;

        // Set table data
        setTableData(apiResponse);

        // Extract stage and reviewer info from the first data entry
        if (apiResponse.data && apiResponse.data.length > 0) {
          const firstEntry = apiResponse.data[0];

          if (firstEntry.ccase_id) {
            setCcaseID(firstEntry.ccase_id);
          }

          if (firstEntry.collection?.stage) {
            setStageInfo(firstEntry.collection.stage);
          }
          if (firstEntry.user) {
            setReviewerInfo(firstEntry.user);
          }
        }

        AJAX()
          .get(_id, res.data.assignments_url)
          .then(res => {
            if (res.data.data.length !== 0) {
              dispatch({ type: ACTIONS.HIDE_HELLPER_SIDE_NAV });
              setReviewers(res.data.data);
            }
          });

        dispatch({
          type: ACTIONS.SET_PAGE_IDS,
          payload: { stage: stage_id, profile: profile_id },
        });
      });
  }, [match.params, reduxState, dispatch]);

  // run once on page load
  useEffect(() => {
    getMainData();
  }, []);

  // on route change
  useEffect(() => {
    if (isInitialMount.current) {
      getMainData();
    } else {
      isInitialMount.current = true;
    }
  }, [match.params.stage_id, match.params.profile_id]);

  useEffect(() => {
    if (reduxState.reviewer && reduxState.reviewer.expiresAt) {
      setCounterData(reduxState.reviewer.expiresAt);
    }
  }, [reduxState]);

  // Handle filter changes from AlphaTable - update URL and state
  const handleFilterChange = filter => {
    setCurrentFilter(filter);

    const { stage_id, profile_id } = match.params;
    const basePath = `${Routes.reviewAdminReviewerAssignments().path}/${stage_id}/${profile_id}`;

    if (filter && Object.keys(filter).length > 0) {
      const filterParam = `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      history.replace(`${basePath}${filterParam}`);
    } else {
      history.replace(basePath);
    }
  };

  // Generate reviewer URL - uses current filter state
  const getReviewerLinkUrl = reviewer => {
    const { stage_id } = match.params;
    const profileId = reviewer.profile?.id || reviewer.profile_id;
    const basePath = `${Routes.reviewAdminReviewerAssignments().path}/${stage_id}/${profileId}`;

    if (currentFilter && Object.keys(currentFilter).length > 0) {
      const filterParam = `?filter=${encodeURIComponent(JSON.stringify(currentFilter))}`;
      return `${basePath}${filterParam}`;
    }
    return basePath;
  };

  const handleTableLinkClick = result => {
    if (result) {
      setScore(result.score);

      AJAX()
        .getReviewCase(_id, reduxState, ccaseID)
        .then(res => {
          if (res.data) {
            setFormData({
              ...res.data.case.form,
              content: result.content || {},
            });
          }
        });
    }

    dispatch({ type: ACTIONS.SHOW_HELLPER_SIDE_NAV });
  };

  // run on unmount
  useEffect(() => {
    return () => {
      AJAX().cancelRequest(_id);
      dispatch({ type: ACTIONS.HIDE_HELLPER_SIDE_NAV });
    };
  }, [_id, dispatch]);

  return (
    <NewAppHOC>
      <style>
        {`
          @media print {
            nav,
            .reviewer-print-view .mainAsideNav,
            .reviewer-print-view .helperAsideNav,
            .reviewer-print-view aside {
              display: none !important;
            }
            [data-name="Main"] {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .reviewer-print-view .myTable__buttons,
            .reviewer-print-view .myTable__searchBox,
            .reviewer-print-view .myTable__filter,
            .reviewer-print-view .myTable__pagination {
              display: none !important;
            }
            .reviewer-print-view .r-hideOnSmall,
            .reviewer-print-view .r-hideOnExtraSmall {
              display: table-cell !important;
            }
            .reviewer-print-view .myTable td:first-child,
            .reviewer-print-view .myTable th:first-child {
              display: none !important;
            }
          }
        `}
      </style>
      <div className="reviewer-print-view">
        <MainAsideNav>
          <div className="__n_bs">
            {/* Back to Dashboard */}
            <Link to="/ui/review_admin/dashboard">
              <Layout flex>
                <div>
                  <Icon
                    iconId="categoriesFull"
                    addClass="c1 __n_bi"
                    style={{ marginRight: ".7rem" }}
                  />
                </div>
                <Title tagName="h1" addClass="c16" removeWeight>
                  {intl.formatMessage({ id: "reviewAdminDashboard" })}
                </Title>
              </Layout>
            </Link>

            {/* Back to Stage */}
            {stageInfo && (
              <Link to={`${Routes.reviewAdminStage().path}/${stageInfo.id}`}>
                <Layout flex>
                  <div>
                    <Icon
                      iconId="categoriesFull"
                      addClass="c1 __n_bi"
                      style={{ marginRight: ".7rem" }}
                    />
                  </div>
                  <Title tagName="h1" addClass="c16" removeWeight>
                    {stageInfo.title}
                  </Title>
                </Layout>
              </Link>
            )}

            {/* Current Reviewer */}
            {reviewerInfo && (
              <div style={{ marginTop: "1.5rem" }}>
                <Layout flex>
                  <div>
                    <Icon iconId="userFull" addClass="c1 __n_bi" style={{ marginRight: ".7rem" }} />
                  </div>
                  <Title tagName="h1" addClass="c16">
                    {reviewerInfo.name || reviewerInfo.email}
                  </Title>
                </Layout>
              </div>
            )}

            {/* All reviewers for stage */}
            <Title style={{ marginBottom: "3.5rem" }}>
              {intl.formatMessage({ id: "stageJury" })}
            </Title>
            {reviewers && reviewers.length !== 0 && (
              <UserNav
                list={reviewers}
                popoverStatsFor="judges"
                getLinkUrl={getReviewerLinkUrl}
                clickable
                selectedId={match.params.profile_id}
                useProjectStats
              />
            )}
          </div>
        </MainAsideNav>
        <Main counter={counterData} addClass="Main__">
          {tableData && (
            <ErrorHOC crashPage>
              <AlphaTable
                data={tableData}
                linkCallBack={handleTableLinkClick}
                initialFilter={initialFilter}
                onFilterChange={handleFilterChange}
              />
            </ErrorHOC>
          )}
        </Main>
        <HelperAsideNav>
          {formData && (
            <div>
              <DisplayScore style={{ marginBottom: "3rem" }} addClass="c10 bc1" score={score} />
              <ReviewForm
                blockFormSubmission={true}
                disableForm={true}
                info={info}
                form={formData}
              />
            </div>
          )}
        </HelperAsideNav>
      </div>
    </NewAppHOC>
  );
}

export default injectIntl(withRouter(ReviewerAssignments));
